/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import ListDataSource from '@react/react-spectrum/ListDataSource';

import { minUIDSupportVersion } from '../../queries';
import { categoryListWithProducts, productsInCategory, productWithVariants, productSearch } from '../../queries/2.3.5';
import {
    cockpitCategoryByParentIDPagination,
    cockpitCategoryByParentUIDPagination,
    cockpitCategoryWithProductsIDPagination,
    cockpitCategoryWithProductsUIDPagination,
    productSearch as productSearch24
} from '../../queries/2.4.2ce';
import { compareSchemaVersions } from '../../utils/compareSchema';

class ProductAdminDataSource extends ListDataSource {
    constructor(props) {
        super();

        const { state } = props;

        this.state = state;
        this.limit = 25;

        this.productPage = 1;
        this.categoryPage = 1;
        this.totalPagesCategories = 1;
        this.totalPagesProducts = 1;
    }

    compareFunc() {
        const { sortField, sortDirection } = this.state;
        return (a, b) => {
            let comparison = 0;

            if (sortField === 'name') {
                comparison = a.name.localeCompare(b.name);
            } else if (sortField === 'price') {
                let priceA = a.price_range ? a.price_range.minimum_price.final_price.value : -1;
                let priceB = b.price_range ? b.price_range.minimum_price.final_price.value : -1;
                if (priceA > priceB) {
                    comparison = 1;
                } else if (priceA < priceB) {
                    comparison = -1;
                }
            }

            return sortDirection === 1 ? comparison : comparison * -1;
        };
    }

    setState(state) {
        this.state = state;
        this.productPage = 1;
        this.categoryPage = 1;
        this.totalPagesCategories = 1;
        this.totalPagesProducts = 1;
        this.reloadData();
    }

    _mapCategory(category) {
        const identifier = category.uid ? category.uid : category.id.toString();

        return {
            ...category,
            identifier
        };
    }

    _mapVariant(baseProduct) {
        return product => {
            // Add attributes to name;
            let name = `${product.product.name} (${product.attributes.map(attribute => attribute.label).join(', ')})`;

            return {
                ...product.product,
                name,
                base_product_sku: baseProduct.sku
            };
        };
    }

    _parseProductVariantResponse(result) {
        if (
            !result.data.products ||
            !result.data.products.items ||
            result.data.products.items.length === 0 ||
            result.data.products.items[0].variants.length === 0
        ) {
            return [];
        }

        const baseProduct = result.data.products.items[0];
        return baseProduct.variants.map(this._mapVariant(baseProduct)).sort(this.compareFunc());
    }

    _parseProductSearchResponse(result) {
        if (!result.data.products || result.data.products.length === 0 || result.data.products.items.length === 0) {
            return [];
        }

        this.totalPagesProducts = result.data.products.page_info.total_pages;

        return result.data.products.items;
    }

    async loadProductSearch() {
        const { sortField, sortDirection, searchTerm } = this.state;
        const { productPage } = this;

        if (productPage > this.totalPagesProducts) {
            return [];
        }

        try {
            const gqlClient = this.state.config.gqlClient;
            const result = await gqlClient.query({
                query: this.state.config.supportUID ? productSearch24 : productSearch,
                variables: {
                    pageSize: this.limit,
                    page: productPage,
                    searchTerm,
                    sort: {
                        [sortField]: sortDirection === 1 ? 'ASC' : 'DESC'
                    }
                }
            });

            this.productPage = productPage + 1;
            return this._parseProductSearchResponse(result);
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    async loadVariants(sku) {
        try {
            const gqlClient = this.state.config.gqlClient;
            const result = await gqlClient.query({
                query: productWithVariants,
                variables: { sku }
            });

            return this._parseProductVariantResponse(result);
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    async queryCategoriesWithoutPagination(identifier, productPage) {
        const gqlClient = this.state.config.gqlClient;
        const { sortField, sortDirection } = this.state;

        // Query categories and products
        let variables = {
            pageSize: this.limit,
            page: productPage,
            id: String(identifier),
            sort: {
                [sortField]: sortDirection === 1 ? 'ASC' : 'DESC'
            }
        };

        return gqlClient.query({ query: categoryListWithProducts, variables }).then(response => {
            return {
                categories: response.data.categoryList[0].children.map(this._mapCategory),
                products: response.data.categoryList[0].products.items,
                // Without pagination, we only have a single category page
                totalPagesCategories: 1,
                totalPagesProducts: response.data.categoryList[0].products.page_info.total_pages
            };
        });
    }

    async queryProductsInCategory235(identifier, productPage) {
        const gqlClient = this.state.config.gqlClient;
        const { sortField, sortDirection } = this.state;

        // Query products
        let variables = {
            pageSize: this.limit,
            page: productPage,
            id: String(identifier),
            sort: {
                [sortField]: sortDirection === 1 ? 'ASC' : 'DESC'
            }
        };

        return gqlClient.query({ query: productsInCategory, variables }).then(response => {
            return {
                categories: [],
                products: response.data.categoryList[0].products.items,
                totalPagesCategories: 1,
                totalPagesProducts: response.data.categoryList[0].products.page_info.total_pages
            };
        });
    }

    async queryCategoriesWithPagination(identifier, categoryPage) {
        const gqlClient = this.state.config.gqlClient;

        // Query categories
        let variables = {
            pageSize: this.limit,
            page: categoryPage,
            id: String(identifier),
            uid: identifier
        };

        let categoryQuery = cockpitCategoryByParentIDPagination;
        if (this.state.config.supportUID) {
            categoryQuery = cockpitCategoryByParentUIDPagination;
        }

        return gqlClient.query({ query: categoryQuery, variables }).then(response => {
            // Get total pages of categories
            const totalPages = response ? response.data.categories.page_info.total_pages : -1;

            // Return categories
            return {
                categories: response.data.categories.items.map(this._mapCategory),
                totalPagesCategories: totalPages
            };
        });
    }

    async queryProductsInCategory242(identifier, productPage) {
        const gqlClient = this.state.config.gqlClient;
        const { sortField, sortDirection } = this.state;

        // Query products
        let variables = {
            pageSize: this.limit,
            page: productPage,
            id: String(identifier),
            uid: identifier,
            sort: {
                [sortField]: sortDirection === 1 ? 'ASC' : 'DESC'
            }
        };

        let productsQuery = cockpitCategoryWithProductsIDPagination;
        if (this.state.config.supportUID) {
            productsQuery = cockpitCategoryWithProductsUIDPagination;
        }

        return gqlClient.query({ query: productsQuery, variables }).then(response => {
            // Get total pages of products
            const totalPages = response ? response.data.categories.items[0].products.page_info.total_pages : -1;

            // Return products
            return {
                products: response.data.categories.items[0].products.items,
                totalPagesProducts: totalPages
            };
        });
    }

    async loadProductsInCategory(identifier) {
        let useCategoryPagination = true;
        if (compareSchemaVersions(this.state.config.schemaVersion, minUIDSupportVersion) === -1) {
            useCategoryPagination = false;
        }

        const categoryPage = this.categoryPage;
        const productPage = this.productPage;

        try {
            let categoriesResult = Promise.resolve({ categories: [], totalPagesCategories: -1 });
            let productsResult = Promise.resolve({ products: [], totalPagesProducts: -1 });

            if (!useCategoryPagination && productPage === 1) {
                categoriesResult = this.queryCategoriesWithoutPagination(identifier, productPage);
            }

            if (!useCategoryPagination && productPage > 1 && productPage <= this.totalPagesProducts) {
                productsResult = this.queryProductsInCategory235(identifier, productPage);
            }

            if (useCategoryPagination && categoryPage <= this.totalPagesCategories) {
                categoriesResult = this.queryCategoriesWithPagination(identifier, categoryPage);
            }

            if (useCategoryPagination && productPage <= this.totalPagesProducts) {
                productsResult = this.queryProductsInCategory242(identifier, productPage);
            }

            // Wait until both queries are done and merge into single object
            const queryResult = Object.assign({}, ...(await Promise.all([productsResult, categoriesResult])));

            const { categories, totalPagesCategories } = queryResult;

            // Array for collecting categories and products
            const items = [];

            // Increase category page
            this.categoryPage = categoryPage + 1;
            if (totalPagesCategories >= 1) {
                this.totalPagesCategories = totalPagesCategories;
            }
            items.push(...categories);

            // Special cases:
            // Always add products if we do not support category pagination, so products are returned every time.
            // If category pagination is supported, start returning products only together with the last category page.
            // This ensures that we maintain a global order in the returned items, so the view can show categories first
            // and products second.
            if (useCategoryPagination && categoryPage < this.totalPagesCategories) {
                return items;
            }

            const { products, totalPagesProducts } = queryResult;

            // Increase product page
            this.productPage = productPage + 1;
            if (totalPagesProducts >= 1) {
                this.totalPagesProducts = totalPagesProducts;
            }
            items.push(...products);

            return items;
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    async load() {
        // Reset pagination
        this.categoryPage = 1;
        this.productPage = 1;
        this.totalPagesCategories = 1;
        this.totalPagesProducts = 1;

        // Display config selection if no config is selected yet
        if (!this.state.config) {
            // Return a sorted copy
            return [...this.state.configs].sort(this.compareFunc());
        }

        if (this.state.configurableProduct) {
            return this.loadVariants(this.state.configurableProduct.sku);
        }

        if (this.state.searchTerm) {
            return this.loadProductSearch();
        }

        // Display products and categories
        let identifier = this.state.config.rootCategory;
        if (this.state.categories.length > 0) {
            identifier = this.state.categories[this.state.categories.length - 1].identifier;
        }

        return this.loadProductsInCategory(identifier);
    }

    async loadMore() {
        // No pagination for configs or product variants
        if (this.state.configurableProduct || !this.state.config) {
            return [];
        }

        if (this.state.searchTerm) {
            return this.loadProductSearch();
        }

        let identifier = this.state.config.rootCategory;
        if (this.state.categories.length > 0) {
            identifier = this.state.categories[this.state.categories.length - 1].identifier;
        }

        return this.loadProductsInCategory(identifier);
    }
}

export default ProductAdminDataSource;
